// Animační engine průvodce okny — portováno 1:1 z designového dema (onboarding/Phoenix Onboarding.html).
//
// Stage (mini-shell schémata + přehrávače scén) je čistě imperativní DOM/CSS animace; aby zůstaly
// PŘESNĚ jako v návrhu, držíme původní logiku přehrávačů a časování beze změny. React (wizard komponenta)
// vlastní jen chrome okolo (hlavička, info panel, patička, navigace) — stage řídí tento engine.
//
// Selektory jsou scoped do předaného kořene scén (každé `id` je unikátní v rámci modálu).

/** Počet kroků průvodce (a tím i scén). */
export const ONBOARDING_STEP_COUNT = 8;

/** Detach/dock ikony použité i v info panelu — stejné glyphy jako ve stage, kvůli vizuální shodě. */
export const ICO_DETACH =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M10 4H6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-4"/><path d="M14 4h6v6"/><path d="M20 4l-8 8"/></svg>';
export const ICO_DOCK =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="16" rx="2"/><path d="M12 8v6"/><path d="M9 11l3 3 3-3"/></svg>';

export interface OnboardingEngine {
  /** Zobrazí scénu daného kroku (ostatní skryje). */
  showScene: (index: number) => void;
  /** Spustí animaci scény daného kroku (zruší běžící časovače). */
  play: (index: number) => void;
  /** Zruší všechny časovače a uvolní engine. */
  destroy: () => void;
}

interface SceneCfg {
  deskId: string;
  itemsId?: string;
  histId?: string;
  cntId?: string;
  menuOn?: boolean;
  plocha?: string;
}

interface MwOpts {
  kind?: 'native' | 'legacy';
  x: number;
  y: number;
  w: number;
  h: number;
  tw?: number;
  lines?: number;
  z?: number | null;
  active?: boolean;
  dim?: boolean;
  float?: boolean;
  crumb?: { w: number; active?: boolean }[];
}

interface TabOpts {
  kind?: 'native' | 'legacy';
  label: string;
  count?: number | string;
  active?: boolean;
}

/**
 * Vytvoří animační engine nad kořenovým elementem scén. Postaví všech 8 scén (mini-shell schémata)
 * a vystaví `showScene`/`play`/`destroy`. Veškerá logika přehrávačů odpovídá designovému demu.
 */
export function createOnboardingEngine(scenesRoot: HTMLElement): OnboardingEngine {
  const $ = (sel: string, root?: HTMLElement): HTMLElement | null =>
    (root ?? scenesRoot).querySelector(sel);
  const el = (tag: string, cls?: string | null, html?: string | null): HTMLElement => {
    const e = document.createElement(tag);
    if (cls) e.className = cls;
    if (html != null) e.innerHTML = html;
    return e;
  };

  const clockSVG =
    '<svg viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="1.2"><circle cx="6" cy="6" r="4.4"/><path d="M6 3.5V6l1.7 1" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  const cursorSVG =
    '<svg viewBox="0 0 16 16"><path d="M2 1.5l11 5.2-4.6 1.3-1.3 4.6L2 1.5Z" fill="#2A2018" stroke="#fff" stroke-width="1.1" stroke-linejoin="round"/></svg>';
  const splitSVG =
    '<span class="snap-glyph"><svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"><rect x="3" y="4" width="18" height="16" rx="2"/><path d="M12 4v16"/></svg></span>';
  const userSVG =
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="8" r="3.2"/><path d="M5.5 19a6.6 6.6 0 0 1 13 0"/></svg>';

  /* ---------- shell builder ---------- */
  function shell(o: SceneCfg): HTMLElement {
    const sc = el('div', 'scene');
    const histId = o.histId ? ` id="${o.histId}"` : '';
    const deskId = o.deskId ? ` id="${o.deskId}"` : '';
    const itemsId = o.itemsId ? ` id="${o.itemsId}"` : '';
    const cntId = o.cntId ? ` id="${o.cntId}"` : '';
    sc.innerHTML = `<div class="shell">
         <div class="shell-top"><span class="st-brand"></span><span class="st-menu"${o.menuOn ? ' data-on' : ''}></span><span class="st-menu"></span><span class="st-menu"></span><span class="st-user">${userSVG}</span></div>
         <div class="desk"${deskId}></div>
         <div class="shell-task">
           <span class="tb-hist"${histId}>${clockSVG}</span>
           <span class="tb-div"></span>
           <span class="tb-plocha"><span class="casc"></span><b>Plocha</b><span class="cnt"${cntId}>${o.plocha || '1 okno'}</span></span>
           <span class="tb-div"></span>
           <span class="tb-items"${itemsId}></span>
         </div>
       </div>`;
    return sc;
  }

  /* ---------- mini-window builder ---------- */
  function mw(opts: MwOpts): HTMLElement {
    const o = Object.assign({ kind: 'native', tw: 56, lines: 3 }, opts) as Required<MwOpts>;
    const e = el('div', 'mw');
    e.dataset['kind'] = o.kind;
    e.style.left = o.x + 'px';
    e.style.top = o.y + 'px';
    e.style.width = o.w + 'px';
    e.style.height = o.h + 'px';
    if (o.z != null) e.style.zIndex = String(o.z);
    if (o.active) e.dataset['active'] = '1';
    if (o.dim) e.dataset['dim'] = '1';
    if (o.float) e.dataset['float'] = '1';
    let crumb = '';
    if (o.crumb) {
      crumb =
        '<div class="mw-crumb"><span class="mw-crumb-home"></span>' +
        o.crumb
          .map(
            (s, idx) =>
              (idx ? '<span class="mw-crumb-sep">›</span>' : '') +
              `<span class="mw-crumb-seg"${s.active ? ' data-active' : ''} style="width:${s.w}px"></span>`
          )
          .join('') +
        '</div>';
    }
    let lines = '';
    const ws = [82, 60, 72, 50, 66];
    for (let i = 0; i < o.lines; i++)
      lines += `<span class="mw-line" style="width:${ws[i % 5]}%"></span>`;
    e.innerHTML =
      crumb +
      `<div class="mw-bar">
         <span class="mw-chip" data-kind="${o.kind}">${o.kind === 'native' ? 'P' : 'M'}</span>
         <span class="mw-tl" style="width:${o.tw}px"></span>
         <span class="mw-ctrls"><i class="mw-c mw-detach">${ICO_DETACH}</i><i class="mw-c mw-min"></i><i class="mw-c mw-max"></i><i class="mw-c mw-close"></i></span>
       </div>
       <div class="mw-body">${lines}</div>`;
    return e;
  }

  function tab(opts: TabOpts): HTMLElement {
    const o = Object.assign({ kind: 'native' }, opts) as Required<TabOpts>;
    const t = el('div', 'ob-tab');
    if (o.active) t.dataset['active'] = '1';
    t.innerHTML =
      `<span class="tab-ico" data-kind="${o.kind}">${o.kind === 'native' ? 'P' : 'M'}</span>` +
      `<span class="tab-label">${o.label}</span>` +
      (o.count ? `<span class="tab-count">${o.count}</span>` : '');
    return t;
  }

  function tag(d: HTMLElement, text: string, style?: Partial<CSSStyleDeclaration>): HTMLElement {
    const a = el('div', 'scene-tag', text);
    Object.assign(a.style, style || {});
    d.appendChild(a);
    return a;
  }
  const show = (e: HTMLElement | null) => {
    if (e) e.style.opacity = '1';
  };
  const hide = (e: HTMLElement | null) => {
    if (e) e.style.opacity = '0';
  };
  function cursor(d: HTMLElement): HTMLElement {
    const c = el('div', 'ob-cursor');
    c.innerHTML = cursorSVG;
    d.appendChild(c);
    return c;
  }
  function moveCur(c: HTMLElement, x: number, y: number) {
    c.style.left = x + 'px';
    c.style.top = y + 'px';
  }

  /* ---------- timers ---------- */
  let timers: ReturnType<typeof setTimeout>[] = [];
  const after = (ms: number, fn: () => void) => timers.push(setTimeout(fn, ms));
  const clearTimers = () => {
    timers.forEach(clearTimeout);
    timers = [];
  };
  function sizeOf(id: string) {
    const d = $('#' + id);
    return { w: d ? d.clientWidth : 0, h: d ? d.clientHeight : 0 };
  }

  /* ======================================================
     SCENE PLAYERS — beze změny oproti návrhu
     ====================================================== */
  const players: (() => void)[] = [
    /* ---- 0 · welcome / anatomy ---- */
    function () {
      const d = $('#desk0');
      if (!d) return;
      d.innerHTML = '';
      const { w, h } = sizeOf('desk0');
      const W = Math.round(w * 0.5),
        H = Math.round(h * 0.6);
      const win = mw({
        kind: 'native',
        x: Math.round((w - W) / 2),
        y: Math.round((h - H) / 2 - 2),
        w: W,
        h: H,
        tw: 58,
        active: true,
        crumb: [{ w: 14 }, { w: 18, active: true }],
        lines: 3,
      });
      win.style.opacity = '0';
      win.style.transform = 'scale(0.96)';
      d.appendChild(win);
      after(60, () => {
        win.style.transition = 'opacity .4s var(--phx-ease-out), transform .4s var(--phx-ease-out)';
        win.style.opacity = '1';
        win.style.transform = 'scale(1)';
      });
      const mk = (txt: string, st: Partial<CSSStyleDeclaration>) => {
        const a = el('div', 'anat', `<span class="anat-tag">${txt}</span>`);
        Object.assign(a.style, st);
        a.style.opacity = '0';
        a.style.transition = 'opacity .35s';
        d.appendChild(a);
        return a;
      };
      const a1 = mk('↑ menu', { left: '8px', top: '6px' });
      const a2 = mk('plocha', { left: '8px', top: '48%' });
      const a3 = mk('taskbar · historie ↓', { left: '8px', bottom: '6px' });
      after(420, () => show(a1));
      after(620, () => show(a2));
      after(820, () => show(a3));
    },

    /* ---- 1 · plocha: windows over each other + reveal + breadcrumb ---- */
    function () {
      const d = $('#desk1');
      const cnt = $('#cnt1');
      if (!d || !cnt) return;
      d.innerHTML = '';
      const { w, h } = sizeOf('desk1');
      const W = Math.round(w * 0.66),
        H = Math.round(h * 0.84);
      const x = Math.round((w - W) / 2),
        y = Math.round((h - H) / 2);
      const kinds: ('native' | 'legacy')[] = ['legacy', 'native', 'legacy'];
      const lbl = (n: number) => (n === 1 ? '1 okno' : n <= 4 ? n + ' okna' : n + ' oken');
      const wins: HTMLElement[] = [];
      for (let k = 0; k < 3; k++) {
        const win = mw({
          kind: kinds[k],
          x,
          y,
          w: W,
          h: H,
          tw: 48,
          crumb: [{ w: 13 }, { w: 18 }, { w: 15, active: true }],
          lines: 3,
          z: 10 + k,
        });
        win.style.opacity = '0';
        win.style.transform = 'scale(0.985)';
        d.appendChild(win);
        wins.push(win);
      }
      cnt.textContent = '—';
      const cur = cursor(d);
      const tag0 = tag(d, 'okna přes sebe', { left: x + 6 + 'px', top: y - 3 + 'px' });
      const tagS = tag(d, '3 okna pod sebou', { left: x + 6 + 'px', top: y - 3 + 'px' });
      const tagB = tag(d, 'zpět drobkem', { left: x + 6 + 'px', top: y - 3 + 'px' });
      const reveal = (k: number) => {
        const win = wins[k];
        win.style.transition = 'opacity .4s var(--phx-ease-out), transform .4s var(--phx-ease-out)';
        win.style.opacity = '1';
        win.style.transform = 'none';
        cnt.textContent = lbl(k + 1);
      };
      after(300, () => reveal(0));
      after(1200, () => reveal(1));
      after(2100, () => {
        reveal(2);
        show(tag0);
      });
      /* fan out → reveal there are several hidden underneath */
      after(3300, () => {
        hide(tag0);
        wins.forEach((wn, k) => {
          wn.style.left = x + k * 26 + 'px';
          wn.style.top = y + k * 19 + 'px';
          if (k < 2) {
            wn.dataset['dim'] = '1';
            delete wn.dataset['active'];
          }
        });
        wins[2].dataset['active'] = '1';
        show(tagS);
      });
      after(4700, () => {
        hide(tagS);
        wins.forEach((wn) => {
          wn.style.left = x + 'px';
          wn.style.top = y + 'px';
          delete wn.dataset['dim'];
        });
      });
      /* breadcrumb back beat */
      after(5450, () => {
        const top = wins[2];
        const seg = top.querySelectorAll<HTMLElement>('.mw-crumb-seg')[0];
        cur.style.opacity = '1';
        moveCur(cur, x + 22, y + 2);
        if (seg) seg.dataset['hot'] = '1';
      });
      after(6300, () => {
        const top = wins[2];
        cur.dataset['grab'] = '1';
        const crumb = top.querySelector('.mw-crumb');
        if (crumb)
          crumb.innerHTML =
            '<span class="mw-crumb-home"></span><span class="mw-crumb-seg" data-active style="width:18px"></span>';
        const ln = top.querySelectorAll<HTMLElement>('.mw-line');
        if (ln[0]) ln[0].style.width = '42%';
        show(tagB);
      });
      after(7200, () => {
        hide(cur);
        hide(tagB);
      });
    },

    /* ---- 2 · window sizes: full vs small + resize + maximize ---- */
    function () {
      const d = $('#deskSize');
      if (!d) return;
      d.innerHTML = '';
      const { w, h } = sizeOf('deskSize');
      const cnt = $('#cntSize');
      const full = mw({
        kind: 'legacy',
        x: 0,
        y: 0,
        w: w,
        h: h,
        tw: 54,
        active: true,
        crumb: [{ w: 14 }, { w: 18, active: true }],
        lines: 4,
      });
      d.appendChild(full);
      const cur = cursor(d);
      const SW = Math.round(w * 0.52),
        SH = Math.round(h * 0.58);
      const sx = Math.round(w * 0.24),
        sy = Math.round(h * 0.16);
      let small: HTMLElement;
      const tagFull = tag(d, 'přes celou plochu', { left: '8px', top: h - 18 + 'px' });
      const tagSmall = tag(d, 'malé okno · stále překrývá', {
        left: sx + 'px',
        top: sy - 14 + 'px',
      });
      const tagBig = tag(d, 'zvětšit taháním', {});
      const tagMax = tag(d, 'maximalizovat', { left: '8px', top: '6px' });
      after(350, () => show(tagFull));
      after(1500, () => {
        hide(tagFull);
        small = mw({
          kind: 'native',
          x: sx,
          y: sy,
          w: SW,
          h: SH,
          tw: 46,
          active: true,
          crumb: [{ w: 13 }, { w: 16, active: true }],
          z: 30,
        });
        small.dataset['pop'] = '1';
        d.appendChild(small);
        full.dataset['dim'] = '1';
        delete full.dataset['active'];
        if (cnt) cnt.textContent = '2 okna';
        show(tagSmall);
      });
      after(2700, () => {
        hide(tagSmall);
        delete small.dataset['pop'];
        cur.style.opacity = '1';
        cur.dataset['grab'] = '1';
        moveCur(cur, sx + SW - 5, sy + SH - 5);
        Object.assign(tagBig.style, { left: sx + 'px', top: sy + SH - 2 + 'px' });
      });
      after(3200, () => {
        const nw = Math.round(w * 0.66),
          nh = Math.round(h * 0.74);
        const nx = Math.round(w * 0.17),
          ny = Math.round(h * 0.12);
        small.style.left = nx + 'px';
        small.style.top = ny + 'px';
        small.style.width = nw + 'px';
        small.style.height = nh + 'px';
        moveCur(cur, nx + nw - 5, ny + nh - 5);
        show(tagBig);
      });
      after(4300, () => {
        hide(tagBig);
        const mx = small.querySelector<HTMLElement>('.mw-max');
        if (mx) mx.dataset['hot'] = '1';
        delete cur.dataset['grab'];
        moveCur(
          cur,
          parseFloat(small.style.left) + parseFloat(small.style.width) - 15,
          parseFloat(small.style.top) + 4
        );
      });
      after(5100, () => {
        const mx = small.querySelector<HTMLElement>('.mw-max');
        if (mx) delete mx.dataset['hot'];
        cur.dataset['grab'] = '1';
        small.dataset['dock'] = '1';
        small.style.left = '0px';
        small.style.top = '0px';
        small.style.width = w + 'px';
        small.style.height = h + 'px';
        show(tagMax);
        moveCur(cur, w - 15, 6);
      });
      after(5900, () => {
        hide(cur);
        hide(tagMax);
        delete small.dataset['dock'];
      });
    },

    /* ---- 3 · opening from menu ---- */
    function () {
      const d = $('#desk2');
      const items = $('#items2');
      if (!d || !items) return;
      d.innerHTML = '';
      items.innerHTML = '';
      const { w, h } = sizeOf('desk2');
      const cnt = $('#cnt2');
      const PW = Math.round(w * 0.6),
        PH = Math.round(h * 0.82);
      /* two docked windows over each other → plocha shows 2 */
      const back = mw({
        kind: 'legacy',
        x: 6,
        y: 6,
        w: PW,
        h: PH,
        tw: 48,
        dim: true,
        crumb: [{ w: 14 }, { w: 16, active: true }],
        lines: 3,
        z: 10,
      });
      const plocha = mw({
        kind: 'legacy',
        x: 6,
        y: 6,
        w: PW,
        h: PH,
        tw: 48,
        active: true,
        crumb: [{ w: 14 }, { w: 16, active: true }],
        lines: 3,
        z: 11,
      });
      d.appendChild(back);
      d.appendChild(plocha);
      if (cnt) cnt.textContent = '2 okna';
      /* one separate floating window already open (stays) */
      const FW = Math.round(w * 0.4),
        FH = Math.round(h * 0.46);
      const fx = w - FW - 8,
        fy = h - FH - 8;
      const flo = mw({ kind: 'native', x: fx, y: fy, w: FW, h: FH, tw: 42, float: true, z: 30 });
      d.appendChild(flo);
      items.appendChild(tab({ kind: 'native', label: 'Barcelona' }));
      const cur = cursor(d);
      const pop = el('div', 'ob-pop');
      pop.dataset['anchor'] = 'top';
      pop.innerHTML =
        '<div class="ob-pop-head">Produkty</div>' +
        (
          [
            ['native', 30],
            ['legacy', 24],
          ] as [string, number][]
        )
          .map(
            ([k, lw]) =>
              `<div class="ob-pop-row"><span class="ob-pop-chip" data-kind="${k}">${k === 'native' ? 'P' : 'M'}</span><span class="ob-pop-lbl" style="width:${lw}px"></span><span class="ob-pop-detach">${ICO_DETACH}</span></div>`
          )
          .join('');
      pop.style.left = '12px';
      pop.style.top = '2px';
      d.appendChild(pop);
      const tagRep = tag(d, 'plocha přepsána · 2 → 1', { left: '10px', top: PH - 18 + 'px' });
      const tagSep = tag(d, ICO_DETACH + ' → druhé samostatné okno', {
        left: Math.round(w * 0.18) + 'px',
        top: Math.round(h * 0.2) - 14 + 'px',
      });
      const rows = pop.querySelectorAll<HTMLElement>('.ob-pop-row');
      after(350, () => {
        cur.style.opacity = '1';
        moveCur(cur, 42, 4);
        pop.dataset['show'] = '1';
      });
      after(1000, () => {
        rows[0].dataset['hot'] = '1';
        moveCur(cur, 44, 26);
      });
      after(1650, () => {
        /* open in plocha → replaces it: 2 → 1, content changes */
        cur.dataset['grab'] = '1';
        delete rows[0].dataset['hot'];
        back.remove();
        plocha.dataset['kind'] = 'native';
        const ch = plocha.querySelector<HTMLElement>('.mw-chip');
        if (ch) {
          ch.dataset['kind'] = 'native';
          ch.textContent = 'P';
        }
        plocha.dataset['swap'] = '1';
        const crumb = plocha.querySelector('.mw-crumb');
        if (crumb)
          crumb.innerHTML =
            '<span class="mw-crumb-home"></span><span class="mw-crumb-seg" style="width:15px"></span><span class="mw-crumb-sep">›</span><span class="mw-crumb-seg" data-active style="width:20px"></span>';
        const ln = plocha.querySelectorAll<HTMLElement>('.mw-line');
        if (ln[0]) ln[0].style.width = '40%';
        if (ln[1]) ln[1].style.width = '72%';
        if (cnt) cnt.textContent = '1 okno';
        show(tagRep);
      });
      after(2350, () => {
        hide(tagRep);
        delete plocha.dataset['swap'];
      });
      after(2750, () => {
        delete cur.dataset['grab'];
        rows[1].dataset['hot'] = '1';
        const dt = rows[1].querySelector<HTMLElement>('.ob-pop-detach');
        if (dt) dt.dataset['hot'] = '1';
        moveCur(cur, 96, 40);
      });
      after(3400, () => {
        /* open as separate → a SECOND separate window appears */
        cur.dataset['grab'] = '1';
        pop.dataset['show'] = '';
        const nw = mw({
          kind: 'legacy',
          x: Math.round(w * 0.18),
          y: Math.round(h * 0.2),
          w: Math.round(w * 0.42),
          h: Math.round(h * 0.5),
          tw: 44,
          float: true,
          z: 31,
        });
        nw.dataset['pop'] = '1';
        d.appendChild(nw);
        items.appendChild(tab({ kind: 'legacy', label: 'Termíny' }));
        show(tagSep);
        hide(cur);
      });
      after(4200, () => {
        hide(tagSep);
      });
    },

    /* ---- 4 · samostatné okno (detach + return to plocha) ---- */
    function () {
      const d = $('#desk3');
      const items = $('#items3');
      const cnt = $('#cnt3');
      if (!d || !items || !cnt) return;
      d.innerHTML = '';
      items.innerHTML = '';
      const { w, h } = sizeOf('desk3');
      /* both maximized, exactly over each other */
      const back = mw({
        kind: 'legacy',
        x: 0,
        y: 0,
        w: w,
        h: h,
        tw: 52,
        dim: true,
        crumb: [{ w: 14 }, { w: 18, active: true }],
        lines: 4,
        z: 10,
      });
      const top = mw({
        kind: 'native',
        x: 0,
        y: 0,
        w: w,
        h: h,
        tw: 52,
        active: true,
        crumb: [{ w: 13 }, { w: 16, active: true }],
        lines: 4,
        z: 11,
      });
      d.appendChild(back);
      d.appendChild(top);
      cnt.textContent = '2 okna';
      const cur = cursor(d);
      const detachBtn = top.querySelector<HTMLElement>('.mw-detach')!;
      const tagF = tag(d, 'samostatné okno', {});
      const tagR = tag(d, 'vrátit do plochy', {});
      const FW = Math.round(w * 0.56),
        FH = Math.round(h * 0.62);
      const fx = w - FW - 8,
        fy = h - FH - 8;
      let tabEl: HTMLElement | undefined;
      /* 1 · highlight detach */
      after(500, () => {
        cur.style.opacity = '1';
        moveCur(cur, w - 18, 9);
        detachBtn.dataset['hot'] = '1';
      });
      /* 2 · detach out */
      after(1600, () => {
        delete detachBtn.dataset['hot'];
        cur.dataset['grab'] = '1';
        top.dataset['float'] = '1';
        top.dataset['pop'] = '1';
        detachBtn.innerHTML = ICO_DOCK;
        top.style.left = fx + 'px';
        top.style.top = fy + 'px';
        top.style.width = FW + 'px';
        top.style.height = FH + 'px';
        moveCur(cur, fx + 24, fy + 9);
        cnt.textContent = '1 okno';
        delete back.dataset['dim'];
        back.dataset['active'] = '1';
        tabEl = tab({ kind: 'native', label: 'Barcelona', active: true });
        tabEl.dataset['enter'] = '1';
        items.appendChild(tabEl);
        after(40, () => {
          if (tabEl) delete tabEl.dataset['enter'];
        });
        Object.assign(tagF.style, { left: fx + 'px', top: fy - 14 + 'px' });
        show(tagF);
      });
      after(2700, () => {
        delete top.dataset['pop'];
        hide(tagF);
      });
      /* 3 · return to its original plocha — re-highlight the button that does it */
      after(3300, () => {
        delete cur.dataset['grab'];
        moveCur(cur, fx + FW - 18, fy + 9);
        detachBtn.dataset['hot'] = '1';
        Object.assign(tagR.style, { left: fx + 'px', top: fy - 14 + 'px' });
        show(tagR);
      });
      after(3850, () => {
        delete detachBtn.dataset['hot'];
        cur.dataset['grab'] = '1';
        top.dataset['dock'] = '1';
        delete top.dataset['float'];
        detachBtn.innerHTML = ICO_DETACH;
        top.style.left = '0px';
        top.style.top = '0px';
        top.style.width = w + 'px';
        top.style.height = h + 'px';
        back.dataset['dim'] = '1';
        delete back.dataset['active'];
        top.dataset['active'] = '1';
        cnt.textContent = '2 okna';
        if (tabEl) {
          tabEl.dataset['enter'] = '1';
          const ref = tabEl;
          setTimeout(() => ref.remove(), 240);
        }
        moveCur(cur, w - 18, 9);
      });
      after(4700, () => {
        delete top.dataset['dock'];
        hide(cur);
        hide(tagR);
      });
    },

    /* ---- 5 · snapping → víc oken naráz ---- */
    function () {
      const d = $('#desk4');
      const items = $('#items4');
      if (!d || !items) return;
      d.innerHTML = '';
      items.innerHTML = '';
      const { w, h } = sizeOf('desk4');
      const HW = Math.round(w / 2),
        HH = Math.round(h / 2);
      const A = mw({
        kind: 'native',
        x: Math.round(w * 0.16),
        y: Math.round(h * 0.2),
        w: Math.round(w * 0.44),
        h: Math.round(h * 0.52),
        tw: 42,
        float: true,
        active: true,
        z: 20,
      });
      const B = mw({
        kind: 'legacy',
        x: Math.round(w * 0.48),
        y: Math.round(h * 0.32),
        w: Math.round(w * 0.42),
        h: Math.round(h * 0.5),
        tw: 42,
        float: true,
        z: 19,
      });
      d.appendChild(A);
      d.appendChild(B);
      items.appendChild(tab({ kind: 'native', label: 'Barcelona', active: true }));
      items.appendChild(tab({ kind: 'legacy', label: 'Termíny' }));
      const cur = cursor(d);
      const snap = el('div', 'ob-snap');
      snap.innerHTML = splitSVG;
      d.appendChild(snap);
      const tagH = tag(d, 'hrana → půlka', { left: '8px', top: HH - 8 + 'px' });
      const tagQ = tag(d, 'roh → čtvrtina', { left: HW + 8 + 'px', top: '6px' });
      const tagN = tag(d, 'dvě agendy naráz', { left: HW - 44 + 'px', top: h - 18 + 'px' });
      const g = 18;
      /* A → left half */
      after(350, () => {
        cur.style.opacity = '1';
        cur.dataset['grab'] = '1';
        A.style.zIndex = '60';
        moveCur(cur, A.offsetLeft + g, A.offsetTop + 4);
      });
      after(800, () => {
        A.style.left = '8px';
        A.style.top = Math.round(h * 0.14) + 'px';
        moveCur(cur, g + 8, Math.round(h * 0.14) + 4);
      });
      after(1250, () => {
        Object.assign(snap.style, {
          left: '0px',
          top: '0px',
          width: HW + 'px',
          height: h + 'px',
          opacity: '1',
        });
      });
      after(1750, () => {
        snap.style.opacity = '0';
        A.dataset['dock'] = '1';
        A.style.left = '0px';
        A.style.top = '0px';
        A.style.width = HW + 'px';
        A.style.height = h + 'px';
        hide(cur);
        show(tagH);
      });
      /* B → top-right quarter */
      after(2550, () => {
        delete A.dataset['dock'];
        cur.style.opacity = '1';
        cur.dataset['grab'] = '1';
        B.style.zIndex = '60';
        moveCur(cur, B.offsetLeft + g, B.offsetTop + 4);
      });
      after(3000, () => {
        B.style.left = Math.round(w * 0.62) + 'px';
        B.style.top = '6px';
        moveCur(cur, Math.round(w * 0.62) + g, 10);
      });
      after(3450, () => {
        Object.assign(snap.style, {
          left: HW + 'px',
          top: '0px',
          width: w - HW + 'px',
          height: HH + 'px',
          opacity: '1',
        });
      });
      after(3950, () => {
        snap.style.opacity = '0';
        B.dataset['dock'] = '1';
        B.style.left = HW + 'px';
        B.style.top = '0px';
        B.style.width = w - HW + 'px';
        B.style.height = HH + 'px';
        hide(cur);
        show(tagQ);
        show(tagN);
      });
      after(4700, () => {
        delete B.dataset['dock'];
      });
    },

    /* ---- 6 · history of opened windows ---- */
    function () {
      const d = $('#desk5');
      const items = $('#items5');
      if (!d || !items) return;
      d.innerHTML = '';
      items.innerHTML = '';
      const histBtn = $('#hist5');
      const cnt = $('#cnt5');
      const { w, h } = sizeOf('desk5');
      const W = Math.round(w * 0.6),
        H = Math.round(h * 0.8);
      const x = Math.round((w - W) / 2),
        y = Math.round((h - H) / 2);
      /* just the plocha — two windows over each other (count 2), no separate windows */
      const back = mw({
        kind: 'legacy',
        x: x,
        y: y,
        w: W,
        h: H,
        tw: 48,
        dim: true,
        crumb: [{ w: 14 }, { w: 18, active: true }],
        z: 10,
      });
      const win = mw({
        kind: 'native',
        x: x,
        y: y,
        w: W,
        h: H,
        tw: 48,
        active: true,
        crumb: [{ w: 14 }, { w: 18, active: true }],
        z: 11,
      });
      d.appendChild(back);
      d.appendChild(win);
      if (cnt) cnt.textContent = '2 okna';
      const cur = cursor(d);
      const pop = el('div', 'ob-pop');
      pop.dataset['anchor'] = 'bottom';
      pop.innerHTML =
        '<div class="ob-pop-head">Historie oken</div>' +
        (
          [
            ['native', 30],
            ['legacy', 24],
            ['legacy', 34],
          ] as [string, number][]
        )
          .map(
            ([k, lw]) =>
              `<div class="ob-pop-row"><span class="ob-pop-chip" data-kind="${k}">${k === 'native' ? 'P' : 'M'}</span><span class="ob-pop-lbl" style="width:${lw}px"></span><span class="ob-pop-detach">${ICO_DETACH}</span></div>`
          )
          .join('');
      pop.style.left = '6px';
      pop.style.bottom = '6px';
      d.appendChild(pop);
      const tagR = tag(d, 'znovu otevřeno', {});
      const rows = pop.querySelectorAll<HTMLElement>('.ob-pop-row');
      after(400, () => {
        if (histBtn) histBtn.dataset['hot'] = '1';
        cur.style.opacity = '1';
        moveCur(cur, 16, h - 12);
      });
      after(950, () => {
        pop.dataset['show'] = '1';
        moveCur(cur, 22, h - 46);
      });
      after(1600, () => {
        rows[1].dataset['hot'] = '1';
        moveCur(cur, 34, h - 44);
      });
      after(2200, () => {
        cur.dataset['grab'] = '1';
        pop.dataset['show'] = '';
        if (histBtn) histBtn.removeAttribute('data-hot');
        /* reopen from history → plocha becomes 1 okno + content changes */
        back.remove();
        win.dataset['kind'] = 'legacy';
        const ch = win.querySelector<HTMLElement>('.mw-chip');
        if (ch) {
          ch.dataset['kind'] = 'legacy';
          ch.textContent = 'M';
        }
        win.dataset['swap'] = '1';
        const crumb = win.querySelector('.mw-crumb');
        if (crumb)
          crumb.innerHTML =
            '<span class="mw-crumb-home"></span><span class="mw-crumb-seg" style="width:15px"></span><span class="mw-crumb-sep">›</span><span class="mw-crumb-seg" data-active style="width:20px"></span>';
        const ln = win.querySelectorAll<HTMLElement>('.mw-line');
        if (ln[0]) ln[0].style.width = '40%';
        if (ln[1]) ln[1].style.width = '72%';
        if (cnt) cnt.textContent = '1 okno';
        Object.assign(tagR.style, { left: x + 6 + 'px', top: y - 4 + 'px' });
        show(tagR);
        hide(cur);
      });
      after(3100, () => {
        hide(tagR);
        delete win.dataset['swap'];
      });
    },

    /* ---- 7 · F5 persistence ---- */
    function () {
      const d = $('#desk6');
      const items = $('#items6');
      if (!d || !items) return;
      d.innerHTML = '';
      items.innerHTML = '';
      const { w, h } = sizeOf('desk6');
      const DW = Math.round(w * 0.5),
        DH = Math.round(h * 0.74);
      const docked = mw({ kind: 'legacy', x: 6, y: 6, w: DW, h: DH, tw: 48, dim: true, z: 10 });
      const FW = Math.round(w * 0.44),
        FH = Math.round(h * 0.54);
      const fx = w - FW - 8,
        fy = h - FH - 8;
      const flo = mw({
        kind: 'native',
        x: fx,
        y: fy,
        w: FW,
        h: FH,
        tw: 46,
        float: true,
        active: true,
        z: 20,
      });
      d.appendChild(docked);
      d.appendChild(flo);
      items.appendChild(tab({ kind: 'native', label: 'Barcelona', active: true }));
      const flash = el('div', 'ob-flash');
      d.appendChild(flash);
      const key = el('div', 'ob-key', 'F5');
      d.appendChild(key);
      const toast = el('div', 'ob-toast', '<span class="dot"></span>Rozložení obnoveno');
      d.appendChild(toast);
      after(500, () => {
        key.style.opacity = '1';
      });
      after(850, () => {
        key.dataset['press'] = '1';
      });
      after(1050, () => {
        delete key.dataset['press'];
        key.style.opacity = '0';
        flash.style.opacity = '1';
        d.querySelectorAll<HTMLElement>('.mw').forEach((m) => (m.style.transition = 'none'));
      });
      after(1320, () => {
        d.querySelectorAll<HTMLElement>('.mw').forEach((m) => (m.style.opacity = '0'));
      });
      after(1650, () => {
        d.querySelectorAll<HTMLElement>('.mw').forEach((m) => {
          m.style.transition = 'opacity .3s var(--phx-ease-out)';
          m.style.opacity = '1';
        });
        flash.style.opacity = '0';
      });
      after(1980, () => {
        toast.style.opacity = '1';
        toast.style.transform = 'translateX(-50%) translateY(0)';
      });
      after(3300, () => {
        toast.style.opacity = '0';
        toast.style.transform = 'translateX(-50%) translateY(8px)';
      });
    },
  ];

  /* ---------- build scenes ---------- */
  const sceneCfg: SceneCfg[] = [
    { deskId: 'desk0', menuOn: true, plocha: '1 okno' },
    { deskId: 'desk1', cntId: 'cnt1', menuOn: true, plocha: '—' },
    { deskId: 'deskSize', cntId: 'cntSize', menuOn: true, plocha: '1 okno' },
    { deskId: 'desk2', itemsId: 'items2', cntId: 'cnt2', menuOn: true, plocha: '2 okna' },
    { deskId: 'desk3', itemsId: 'items3', cntId: 'cnt3', menuOn: true, plocha: '2 okna' },
    { deskId: 'desk4', itemsId: 'items4', plocha: '0 oken' },
    { deskId: 'desk5', itemsId: 'items5', histId: 'hist5', cntId: 'cnt5', plocha: '2 okna' },
    { deskId: 'desk6', itemsId: 'items6', plocha: '1 okno' },
  ];
  const scenes = sceneCfg.map((c) => {
    const s = shell(c);
    scenesRoot.appendChild(s);
    return s;
  });

  return {
    showScene(index: number) {
      scenes.forEach((sc, k) => sc.toggleAttribute('data-on', k === index));
    },
    play(index: number) {
      clearTimers();
      const dsk = scenesRoot.querySelector<HTMLElement>('.scene[data-on] .desk');
      if (dsk) void dsk.clientWidth; // force reflow
      if (players[index]) players[index]();
    },
    destroy() {
      clearTimers();
    },
  };
}
